import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SectionHeading } from '../ui/SectionHeading';
import { ChevronDown, BookOpen } from 'lucide-react';
const tomos = [{
  id: 'T1',
  title: 'Tomo 1 — Nosotros antes del bebé',
  subtitle: 'Primer trimestre: nombrar lo que no se dice',
  desc: 'Mapa de emociones paralelo, frases para nombrar miedos sin que pesen, el rol activo de él desde el primer mes, y el primer bloque de cartas de conversación semanales.',
  image: 'https://images.unsplash.com/photo-1518621736915-f3b1c41bfd00?auto=format&fit=crop&w=160&q=60',
  bullets: ['Mapa de emociones paralelo', 'Frase de entrada para miedos', "Rol activo de él (no 'ayuda', proceso propio)"]
}, {
  id: 'T2',
  title: 'Tomo 2 — El equipo que somos',
  subtitle: 'Segundo trimestre: comunicación bajo presión y primeros desacuerdos grandes',
  desc: 'Herramientas prácticas para manejar las expectativas externas, alinear estilos de crianza y resolver discusiones sin lastimar la relación.',
  image: 'https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=160&q=60'
}, {
  id: 'T3',
  title: 'Tomo 3 — Llegando juntos a él',
  subtitle: 'Tercer trimestre: preparación conjunta para la llegada y cierre del tracker de nosotros',
  desc: 'Planificación logística y emocional para el parto y los primeros días. Cómo blindar el equipo antes de que empiece el cansancio extremo.',
  image: 'https://images.unsplash.com/photo-1555252333-9f8e92e65df9?auto=format&fit=crop&w=160&q=60'
}];
export function Tomos() {
  const [expandedId, setExpandedId] = useState<string | null>('T1');
  return <section className="py-12 md:py-24 px-4 md:px-8 max-w-4xl mx-auto" data-id="element-307">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6
    }} className="mb-12 md:mb-16 flex justify-center" data-id="element-308">
        <video src="https://cdn.magicpatterns.com/uploads/ogdtG1UjUWkzkiQb462UuZ/Grabacio%CC%81n%20de%20pantalla%202026-06-16%20a%20la(s)%2020.58.07.mov" autoPlay loop muted playsInline preload="metadata" className="w-full max-w-3xl h-auto rounded-2xl shadow-md border border-brown/10" data-id="element-309" />
      </motion.div>

      <SectionHeading title="CONTENIDO COMPLETO" subtitle="LOS 3 TOMOS: Su hoja de ruta, trimestre por trimestre" data-id="element-310" />

      <div className="text-center mb-6 md:mb-8" data-id="element-311">
        <span className="inline-block bg-terracotta/10 text-terracotta font-sans font-medium px-4 py-2 rounded-full text-xs md:text-sm" data-id="element-312">
          Tocá cada tomo para ver lo que incluye 👆
        </span>
      </div>

      <div className="space-y-3 md:space-y-4" data-id="element-313">
        {tomos.map(tomo => {
        const isExpanded = expandedId === tomo.id;
        return <motion.div key={tomo.id} className={`bg-offwhite rounded-2xl border transition-colors duration-300 overflow-hidden ${isExpanded ? 'border-terracotta' : 'border-brown/10 hover:border-terracotta/50'}`} data-id="element-314">
              <button onClick={() => setExpandedId(isExpanded ? null : tomo.id)} className="w-full text-left p-3 md:p-4 flex items-center justify-between gap-3 md:gap-4" aria-expanded={isExpanded} data-id="element-315">
                <div className="flex items-center gap-3 md:gap-4 min-w-0" data-id="element-316">
                  <div className="relative w-14 h-14 md:w-16 md:h-16 rounded-xl overflow-hidden shrink-0" data-id="element-317">
                    <img src={tomo.image} alt={tomo.title} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-318" />
                    <span className="absolute bottom-0 left-0 bg-terracotta text-offwhite font-serif font-bold text-[10px] md:text-xs px-1.5 py-0.5 rounded-tr-lg" data-id="element-319">
                      {tomo.id}
                    </span>
                  </div>
                  <div className="min-w-0" data-id="element-320">
                    <h3 className="font-serif font-bold text-base md:text-xl text-brown leading-tight" data-id="element-321">
                      {tomo.title}
                    </h3>
                    <p className="font-sans text-xs md:text-sm text-terracotta mt-0.5 md:mt-1 line-clamp-2" data-id="element-322">
                      {tomo.subtitle}
                    </p>
                  </div>
                </div>
                <ChevronDown className={`text-terracotta transition-transform duration-300 shrink-0 ${isExpanded ? 'rotate-180' : ''}`} size={20} data-id="element-323" />
              </button>

              <AnimatePresence data-id="element-324">
                {isExpanded && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} data-id="element-325">
                    <div className="px-4 md:px-6 pb-4 md:pb-5 pt-0 ml-[68px] md:ml-20" data-id="element-326">
                      <p className="font-sans text-sm md:text-base text-brown/80 mb-3 md:mb-4 leading-relaxed" data-id="element-327">
                        {tomo.desc}
                      </p>
                      {tomo.bullets && <ul className="space-y-2" data-id="element-328">
                          {tomo.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-2 font-sans text-xs md:text-sm text-brown/70" data-id="element-329">
                              <BookOpen size={16} className="text-terracotta shrink-0 mt-0.5" data-id="element-330" />
                              {bullet}
                            </li>)}
                        </ul>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>;
      })}
      </div>
    </section>;
}